// utils/countryPrefix.js

import store from 'src/store'

export const SUPPORTED_COUNTRIES = ['us'] // extend as needed

/**
 * Recursively clones a route tree, prepending a country param alias.
 * Used to generate /:country/hotels, /:country/reservation, etc.
 */
export function withCountryPrefix(routes) {
  return routes.map((route) => cloneRouteWithPrefix(route))
}

function cloneRouteWithPrefix(route) {
  const cloned = { ...route }

  // Rename to avoid duplicate named routes (Vue Router requires unique names)
  if (cloned.name) {
    cloned.name = `${cloned.name}__country`
  }

  // Recursively clone children
  if (cloned.children) {
    cloned.children = cloned.children.map((child) => cloneRouteWithPrefix(child))
  }

  return cloned
}

/**
 * Extract country code from a path, e.g. /us/hotels → 'us'
 */
export function extractCountryFromPath(path) {
  const usPrefixedEnabled = store.getters['setting/isUSUrlPrefixed']

  if (!usPrefixedEnabled) return null

  const match = path.match(/^\/([a-z]{2})(\/|$)/)
  if (match && SUPPORTED_COUNTRIES.includes(match[1])) {
    return match[1]
  }
  return null
}

/**
 * Strip country prefix from a path, e.g. /us/hotels → /hotels
 */
export function stripCountryFromPath(path) {
  return path.replace(/^\/[a-z]{2}(\/|$)/, '/') || '/'
}

/**
 * Returns the country-prefixed route name when country is present.
 * e.g. countryRouteName('OtaHome', 'us') → 'OtaHome__country'
 */
export function countryRouteName(baseName, country) {
  return country ? `${baseName}__country` : baseName
}

/**
 * Builds a params object that includes the country key when present.
 * e.g. countryParams('us', { code: 'ABC' }) → { code: 'ABC', country: 'us' }
 */
export function countryParams(country, extra = {}) {
  return country ? { ...extra, country } : extra
}

/**
 * Strips the __country suffix from a route name for base-name comparisons.
 * e.g. baseRouteName('OtaHome__country') → 'OtaHome'
 */
export function baseRouteName(name) {
  return name ? name.replace(/__country$/, '') : name
}
